import { action, makeObservable, override } from 'mobx';
import { GroupingDirective, SortingDirective } from '@list-store';
import type { EventQueueRowData } from '../../socket/stream-types';
import { QueueStore } from '../../queues/store/queue-store';
import { WorkspacePanelWithQueue } from '../../panels/workspace-queue-exists';
import { EventNotificationsStore } from '../../event-notifications/event-notifications-store';
import type { GeoPoint } from '../../location/types';
import { getDistanceFromEvent, injectDistanceFromEvent } from '../../location/event-helpers';

export class EventQueueStore extends QueueStore<EventQueueRowData> {
    public lastDistanceUpdateTime: number = Date.now();
    public lastDistanceUpdateLocation: GeoPoint | null = null;

    constructor({
        manualFilterDirective,
        workspacePanel,
        eventNotificationsStore,
        playAedSoundForEvent,
        sortingDirective,
        groupingDirective,
    }: {
        workspacePanel: WorkspacePanelWithQueue;
        eventNotificationsStore?: EventNotificationsStore;
        playAedSoundForEvent?: (eventId: number) => void;
        manualFilterDirective?: boolean;
        sortingDirective?: SortingDirective<EventQueueRowData>;
        groupingDirective?: GroupingDirective<EventQueueRowData>;
    }) {
        super({
            manualFilterDirective,
            workspacePanel,
            eventNotificationsStore,
            playAedSoundForEvent,
            sortingDirective,
            groupingDirective,
        });
        makeObservable(this);
    }

    @override
    public setRows(rows: EventQueueRowData[], unitLocation?: GeoPoint): void {
        const rowsWithDistanceFromEvent = injectDistanceFromEvent(rows, unitLocation);
        super.setRows(rowsWithDistanceFromEvent);
        this.recordLastUpdate(unitLocation);
    }

    @override
    public upsertRows(rows: EventQueueRowData[], unitLocation?: GeoPoint): void {
        const rowsWithDistanceFromEvent = injectDistanceFromEvent(rows, unitLocation);
        super.upsertRows(rowsWithDistanceFromEvent);
        this.recordLastUpdate(unitLocation);
    }

    @action
    updateDistancesForAllEvents(unitLocation?: GeoPoint): void {
        for (const listItem of this.listStore.data.list) {
            listItem.distanceFromEvent = unitLocation
                ? getDistanceFromEvent(listItem, unitLocation)
                : undefined;
        }
        this.recordLastUpdate(unitLocation);
    }

    private recordLastUpdate(unitLocation?: GeoPoint): void {
        if (unitLocation) {
            this.lastDistanceUpdateTime = Date.now();
            this.lastDistanceUpdateLocation = unitLocation;
        }
    }
}
